import { COUNTRY_EXCEPTIONS } from '@/constants/web-locales.constant';

import { WebLocale } from '@/enums/web-locale';

import { getAxiosCached } from '@/helpers/axios-helper';
import { captureMessageForSentry, Sentry } from '@/helpers/sentry-helper';

export type CountryString = WebLocale | string;

export type LocaleResponse = {
	country: string;
	country_names: Record<string, string>;
	currency: string;
	currency_name: string;
	exposed_url_part: WebLocale;
	full_locale: string;
	i18n_state: 'active' | 'staging';
	is_standard_locale: boolean;
	iso_3166_2: string;
	timezone: string; // e.g. Europe/Berlin
	timezone_offset: number; // e.g. 120
	timezone_abbreviation: string; // e.g. CEST
};

export type Locale = LocaleResponse & { webLocale: WebLocale };

export async function getLocales(): Promise<Locale[]> {
	let availableLocales: Locale[] = [];

	try {
		const response = await getAxiosCached().get(`${JW_CONFIG.API}/locales/state`, {
			params: { platform: 'web' },
		});

		const localesList = response.data;

		if (localesList && localesList.length) {
			availableLocales = localesList.map((data: any) => {
				const webLocale =
					data.exposed_url_part in COUNTRY_EXCEPTIONS
						? COUNTRY_EXCEPTIONS[data.exposed_url_part]
						: (data.exposed_url_part as WebLocale);

				return {
					...data,
					webLocale,
				};
			});
		}
	} catch (error: any) {
		captureMessageForSentry(
			'Main Init Issue:',
			{ error, where: '[locale-heleper.ts] getLocales()' },
			Sentry.Severity.Error
		);
	}

	return availableLocales;
}

export async function getActiveLocales(): Promise<Locale[]> {
	const locales: Locale[] = await getLocales();
	const activeLocales = locales.filter(webLocale => webLocale.i18n_state === 'active');

	if (activeLocales.length) {
		return activeLocales;
	}

	// fallback to local list
	const defaultLocalesJson = await import('@/default-locales.json').then(module => module.default);
	return defaultLocalesJson as unknown as Locale[];
}

export function getLanguage(localeCode: string): string {
	if (localeCode.length < 2) {
		throw new Error(`[getLanguage] localeCode: ${localeCode} is too short`);
	}

	return localeCode.substr(0, 2);
}

/**
 * transform an hypend formatted locale to an underscore one if necessary
 * (E.g. en-US -> en_US)
 *
 * @param hypenedLocale
 *
 * @throws {Error} given parameter is not valid
 *
 * @returns {string} underscoreLocale
 */
export function transformLocaleToUnderscore(hypenedLocale: string): string {
	const validateLocale = /^[a-z]{2}_[A-Z]{2}$/;
	const underscoreLocale = hypenedLocale.replace('-', '_');
	const formattedLocale = underscoreLocale.slice(0, 3) + underscoreLocale.slice(3, 5).toUpperCase();

	if (!validateLocale.test(formattedLocale)) {
		throw new Error('[transformLocaleToUnderscore] given parameter is not valid');
	}

	return formattedLocale;
}

/**
 * extract from a BCP 47 language code to a weblocale
 * (E.g. en-US -> us)
 *
 * @param locale
 *
 * @throws {Error} given parameter is not valid
 *
 * @returns {string} weblocale
 */
export function extractWebLocaleFromLocale(locale: string): string | null {
	const webLocaleRegExp = /^[a-z]{2}[_-]{1}(?<weblocale>[A-Z]{2})/;
	const match = locale.match(webLocaleRegExp);
	const { weblocale = null } = match?.groups || {};
	return weblocale;
}

export function getAvailableLocale(locale: string): string | null {
	const weblocale = extractWebLocaleFromLocale(locale);
	return WebLocale[weblocale?.toLocaleLowerCase() as WebLocale] || null;
}

/**
 * get webLocale from browser when available
 *
 * @returns {string|undefined} webLocale
 */
export function getLocaleFromBrowser(): string | null {
	if (!process.client) {
		return null;
	}
	const browserLangLocale = window?.navigator?.language;

	if (!browserLangLocale) {
		return null;
	}
	return getAvailableLocale(browserLangLocale);
}

/**
 * get webLocale from browser when available
 *
 * @returns {string|null} webLocale
 */
export function getLanguageFromBrowser(): string | null {
	const browserLangLocale = navigator?.language;

	if (!browserLangLocale) {
		return null;
	}
	return getLanguage(browserLangLocale);
}
